// @file Tracks scroll smoothness metrics and jankiness for performance monitoring
import { trackEvent } from '@@/bits/analytics'

interface SmoothnessSample {
  ts: number // performance.now()
  dt: number // ms since last frame
  y: number // scroll position
  dy: number // delta scroll px
}

interface SmoothnessPayload {
  sessionId: string
  startedAt: number // epoch ms
  durationMs: number
  distancePx: number
  avgFps: number
  p95FrameMs: number
  worstFrameMs: number
  slowFramePct: number // % frames slower than targetFrameMs
  jankEvents: number
  p95JankMs: number
  worstJankMs: number
  maxVelocityPxPerS: number
  p95VelocityPxPerS: number
  inputDelayP95Ms?: number
  longTasks: { count: number; worstMs: number; totalMs: number }
  // environment
  viewport: { w: number; h: number; dpr: number }
  device: { mem?: number; cores?: number; rm?: boolean }
  page: { path: string; title: string; hidden: boolean }
  // performance context
  domNodeCount: number
  jsHeapSize?: { used: number; total: number; limit: number }
}

/**
 * ScrollSmoothnessTracker monitors scroll performance in real-time to detect jankiness and measure user experience quality.
 *
 * This class tracks scroll sessions by sampling frame timing data during user scroll interactions. It measures:
 * - Frame rates and dropped frames to detect visual stuttering
 * - Jank events (consecutive slow frames) that cause choppy scrolling
 * - Input delays between user actions and browser response
 * - Long JavaScript tasks that block smooth scrolling
 * - Scroll velocity and distance metrics
 * - System context (device capabilities, DOM complexity, memory usage)
 *
 * The tracker uses a 1% sampling rate to avoid overwhelming analytics backends while still capturing
 * representative performance data. Sessions automatically start on scroll events and end after 200ms
 * of inactivity or when the page becomes hidden.
 *
 * Collected metrics are sent to analytics with a calculated smoothness score (0-100, higher = smoother)
 * to help identify performance regressions and optimize scroll experiences across different devices.
 */
export class ScrollSmoothnessTracker {
  private readonly el: HTMLElement | Window
  private rafId: number | null = null
  private active = false
  private idleTimer: number | null = null
  private readonly idleMs = 200
  private samples: SmoothnessSample[] = []
  private longTasksDuring: { count: number; worstMs: number; totalMs: number } = { count: 0, worstMs: 0, totalMs: 0 }
  private inputDelays: number[] = []
  private sessionStart = 0
  private sessionId = ''

  // Perf observers
  private longTaskObs?: PerformanceObserver
  private eventObs?: PerformanceObserver

  constructor(
    el: HTMLElement | Window = window,
    private targetFrameMs = 1000 / 60, // adapt at runtime below
  ) {
    this.el = el
    this.init()
  }

  private init(): void {
    // Adapt targetFrameMs on high-refresh devices (best effort from rAF cadence once)
    requestAnimationFrame((t0) => {
      requestAnimationFrame((t1) => {
        const dt = t1 - t0
        if (dt > 0 && dt < 16) this.targetFrameMs = Math.max(1000 / 120, dt * 1.25)
      })
    })

    // Start/extend a session on user inputs
    window.addEventListener('wheel', this.onUserScrollStart, { passive: true })
    window.addEventListener('touchmove', this.onUserScrollStart, { passive: true })
    window.addEventListener('scroll', this.onScroll, { passive: true })

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.endSession(/* reason */ 'hidden')
    })

    // Long tasks
    if ('PerformanceObserver' in window) {
      try {
        this.longTaskObs = new PerformanceObserver((list) => {
          if (!this.active) return
          for (const e of list.getEntries()) {
            const dur = e.duration
            this.longTasksDuring.count += 1
            this.longTasksDuring.totalMs += dur
            if (dur > this.longTasksDuring.worstMs) this.longTasksDuring.worstMs = dur
          }
        })
        this.longTaskObs.observe({ type: 'longtask', buffered: true as any })
      } catch {}
      // Event timing (input delay)
      try {
        this.eventObs = new PerformanceObserver((list) => {
          if (!this.active) return
          for (const e of list.getEntries() as PerformanceEventTiming[]) {
            // consider only scroll-relevant events
            if (!['wheel', 'touchmove', 'pointermove'].includes(e.name)) continue
            const delay = (e.processingStart || e.startTime) - e.startTime
            if (delay >= 0) this.inputDelays.push(delay)
          }
        })
        // Include high-priority events during scroll
        this.eventObs.observe({ type: 'event', buffered: true as any })
      } catch {}
    }
  }

  private onUserScrollStart = (): void => {
    if (document.hidden) return
    this.maybeStartOrContinueSession()
  }

  private onScroll = (): void => {
    this.maybeStartOrContinueSession()
  }

  private maybeStartOrContinueSession(): void {
    if (!this.active) {
      // Randomly track 1% of scroll interactions to avoid overwhelming the backend
      if (Math.random() > 0.01) return
      this.beginSession()
    }
    this.bumpIdle()
  }

  private beginSession(): void {
    this.active = true
    this.sessionStart = Date.now()
    this.sessionId = Math.random().toString(36).slice(2)
    this.samples = []
    this.longTasksDuring = { count: 0, worstMs: 0, totalMs: 0 }
    this.inputDelays = []

    let prevTs = performance.now()
    let prevY = this.getScrollY()
    const tick = (ts: number) => {
      if (!this.active) return
      const y = this.getScrollY()
      const dt = ts - prevTs
      const dy = y - prevY
      // Ignore background tabs where dt explodes
      if (dt > 0 && dt < 250) {
        this.samples.push({ ts, dt, y, dy })
      }
      prevTs = ts
      prevY = y
      this.rafId = requestAnimationFrame(tick)
    }
    this.rafId = requestAnimationFrame(tick)

    // Safety flush for very long sessions
    window.setTimeout(() => {
      if (this.active) this.endSession('chunk')
    }, 15000)
  }

  private bumpIdle(): void {
    if (this.idleTimer) window.clearTimeout(this.idleTimer)
    this.idleTimer = window.setTimeout(() => this.endSession('idle'), this.idleMs)
  }

  private endSession(reason: 'idle' | 'hidden' | 'chunk'): void {
    if (!this.active) return
    this.active = false
    // Clear any pending idle timer
    if (this.idleTimer) {
      window.clearTimeout(this.idleTimer)
      this.idleTimer = null
    }
    if (this.rafId != null) cancelAnimationFrame(this.rafId)
    this.rafId = null
    if (!this.samples.length) return

    const payload = this.computeMetrics()
    // Use standard analytics instead of direct POST
    this.trackScrollMetrics(payload)

    // If chunked due to 5s timeout, immediately start a new session if user keeps scrolling
    if (reason === 'chunk') this.beginSession()
  }

  private computeMetrics(): SmoothnessPayload {
    const s = this.samples
    const durationMs = s[s.length - 1].ts - s[0].ts
    const frameTimes = s.slice(1).map((p) => p.dt)
    const velocities = s.slice(1).map((p) => Math.abs(p.dy) / (p.dt / 1000)) // px/s
    const distancePx = s.reduce((acc, cur, i) => (i ? acc + Math.abs(cur.dy) : 0), 0)

    const avgFrame = average(frameTimes)
    const avgFps = 1000 / avgFrame
    const p95FrameMs = pQuantile(frameTimes, 0.95)
    const worstFrameMs = Math.max(...frameTimes)
    const slowFramePct = (100 * frameTimes.filter((dt) => dt > this.targetFrameMs).length) / frameTimes.length

    // Jank = runs of consecutive slow frames; measure burst worst
    let jankEvents = 0
    let curJank = 0
    const jankSizes: number[] = []
    for (const dt of frameTimes) {
      if (dt > this.targetFrameMs) {
        curJank += dt - this.targetFrameMs
      } else if (curJank > 0) {
        jankEvents++
        jankSizes.push(curJank)
        curJank = 0
      }
    }
    if (curJank > 0) {
      jankEvents++
      jankSizes.push(curJank)
    }
    const p95JankMs = jankSizes.length ? pQuantile(jankSizes, 0.95) : 0
    const worstJankMs = jankSizes.length ? Math.max(...jankSizes) : 0

    const maxVelocity = velocities.length ? Math.max(...velocities) : 0
    const p95Velocity = velocities.length ? pQuantile(velocities, 0.95) : 0

    const inputDelayP95Ms = this.inputDelays.length ? pQuantile(this.inputDelays, 0.95) : undefined

    return {
      sessionId: this.sessionId,
      startedAt: this.sessionStart,
      durationMs,
      distancePx,
      avgFps,
      p95FrameMs,
      worstFrameMs,
      slowFramePct,
      jankEvents,
      p95JankMs,
      worstJankMs,
      maxVelocityPxPerS: maxVelocity,
      p95VelocityPxPerS: p95Velocity,
      inputDelayP95Ms,
      longTasks: this.longTasksDuring,
      viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 },
      device: {
        mem: (navigator as any).deviceMemory,
        cores: (navigator as any).hardwareConcurrency,
        rm: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
      },
      page: { path: location.pathname, title: document.title, hidden: document.hidden },
      // performance context
      domNodeCount: this.getDOMNodeCount(),
      jsHeapSize: this.getJSHeapSize(),
    }
  }

  // we recompute every time because DOM nodes should increase as the user scrolls and more posts are lazy loaded
  private getDOMNodeCount(): number {
    return document.getElementsByTagName('*').length
  }

  private getJSHeapSize(): { used: number; total: number; limit: number } | undefined {
    const memory = (performance as any).memory
    if (memory) {
      return {
        used: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
        limit: memory.jsHeapSizeLimit,
      }
    }
    return undefined
  }

  private getScrollY(): number {
    if (this.el === window) return window.scrollY || document.documentElement.scrollTop || 0
    const node = this.el as HTMLElement
    return node.scrollTop
  }

  private trackScrollMetrics(payload: SmoothnessPayload): void {
    try {
      // Calculate scroll smoothness score (0-100, higher is better)
      const score = this.calculateSmoothnessScore(payload)

      // Use the standard analytics utility to track scroll performance metrics
      trackEvent(
        'Surface', // category
        'Scroll performance tracked', // action
        'Scroll jankiness measurement', // label
        Math.round(score), // value (scroll smoothness score 0-100)
        payload, // eventProps - full payload as custom properties
      )
    } catch {
      // swallow errors to avoid disrupting user experience
    }
  }

  private calculateSmoothnessScore(payload: SmoothnessPayload): number {
    const score =
      100 -
      clamp(payload.slowFramePct * 0.6, 0, 60) - // dropped frames hurt most
      clamp((payload.p95FrameMs - this.targetFrameMs) * 1.2, 0, 25) -
      clamp(payload.jankEvents * 0.5, 0, 10) -
      clamp((payload.inputDelayP95Ms || 0) / 4, 0, 5)

    return Math.max(0, score) // Ensure score doesn't go below 0
  }

  // Public helpers
  destroy(): void {
    window.removeEventListener('wheel', this.onUserScrollStart)
    window.removeEventListener('touchmove', this.onUserScrollStart)
    window.removeEventListener('scroll', this.onScroll)
    if (this.rafId != null) cancelAnimationFrame(this.rafId)
    this.longTaskObs?.disconnect()
    this.eventObs?.disconnect()
  }
}

// --- small utils ---
function average(a: number[]): number {
  return a.reduce((x, y) => x + y, 0) / Math.max(1, a.length)
}
function pQuantile(a: number[], q: number): number {
  if (!a.length) return 0
  const b = [...a].sort((x, y) => x - y)
  const idx = Math.min(b.length - 1, Math.max(0, Math.floor(q * (b.length - 1))))
  return b[idx]
}
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}
